import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  IconButton,
  Tooltip,
  Avatar,
  AvatarGroup,
  Divider,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Assignment,
  Schedule,
  AttachMoney,
  Group,
  TrendingUp,
  TrendingDown,
  Warning,
  CheckCircle,
  Error as ErrorIcon,
  Visibility,
  GetApp,
  FilterList,
  Search
} from '@mui/icons-material';
import { analyticsService } from '../../../services/analyticsService';

interface DateRange {
  start: Date;
  end: Date;
}

interface ProjectReportsProps {
  dateRange: DateRange;
}

interface Project {
  id: string;
  name: string;
  client: string;
  status: 'planning' | 'active' | 'on-hold' | 'completed' | 'cancelled';
  progress: number;
  budget: number;
  spent: number;
  startDate: Date;
  endDate: Date;
  teamMembers: string[];
  priority: 'low' | 'medium' | 'high' | 'critical';
  category: 'web' | 'mobile' | 'desktop' | 'integration' | 'consulting';
  health: 'excellent' | 'good' | 'at-risk' | 'critical';
  milestones: {
    total: number;
    completed: number;
  };
  tasks: {
    total: number;
    completed: number;
    inProgress: number;
    pending: number;
  };
}

interface ProjectSummary {
  totalProjects: number;
  activeProjects: number;
  completedProjects: number;
  totalBudget: number;
  totalSpent: number;
  averageProgress: number;
  onTimeDelivery: number;
  budgetUtilization: number;
}

const ProjectReports: React.FC<ProjectReportsProps> = ({ dateRange }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [projectData, setProjectData] = useState<any>(null);
  const [filteredProjects, setFilteredProjects] = useState<Project[]>([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [clientFilter, setClientFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name');

  // Load project data from API
  useEffect(() => {
    const loadProjectReports = async () => {
      try {
        setLoading(true);
        setError(null);

        const filters = {
          dateRange: {
            start: dateRange.start.toISOString(),
            end: dateRange.end.toISOString()
          },
          status: statusFilter !== 'all' ? statusFilter : undefined,
          client: clientFilter !== 'all' ? clientFilter : undefined,
          search: searchTerm || undefined
        };

        const data = await analyticsService.getProjectReports(filters);
        setProjectData(data);
      } catch (err) {
        console.error('Error loading project reports:', err);
        setError('Failed to load project reports data');
      } finally {
        setLoading(false);
      }
    };

    loadProjectReports();
  }, [dateRange, statusFilter, clientFilter, searchTerm]);

  // Transform API data to component format
  const projects: Project[] = projectData?.projects?.map((project: any) => ({
    id: project.id,
    name: project.name,
    client: project.client,
    status: project.status,
    progress: project.progress,
    budget: project.budget,
    spent: project.spent,
    startDate: new Date(project.start_date),
    endDate: new Date(project.end_date),
    teamMembers: project.team_members || [],
    priority: project.priority,
    category: project.category,
    health: project.health,
    milestones: project.milestones || { total: 0, completed: 0 },
    tasks: project.tasks || { total: 0, completed: 0, inProgress: 0, pending: 0 }
  })) || [];

  // Calculate project summary from API data or fallback
  const projectSummary: ProjectSummary = projectData?.summary || {
    totalProjects: projects.length,
    activeProjects: projects.filter(p => p.status === 'active').length,
    completedProjects: projects.filter(p => p.status === 'completed').length,
    totalBudget: projects.reduce((sum, p) => sum + p.budget, 0),
    totalSpent: projects.reduce((sum, p) => sum + p.spent, 0),
    averageProgress: projects.length > 0 ? projects.reduce((sum, p) => sum + p.progress, 0) / projects.length : 0,
    onTimeDelivery: projectData?.summary?.onTimeDelivery || 85,
    budgetUtilization: projects.length > 0 ? 
      (projects.reduce((sum, p) => sum + p.spent, 0) / projects.reduce((sum, p) => sum + p.budget, 0)) * 100 : 0
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'active': return 'primary';
      case 'planning': return 'info';
      case 'on-hold': return 'warning';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getHealthColor = (health: string) => {
    switch (health) {
      case 'excellent': return 'success';
      case 'good': return 'info';
      case 'at-risk': return 'warning';
      case 'critical': return 'error';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getBudgetStatus = (budget: number, spent: number) => {
    const percentage = (spent / budget) * 100;
    if (percentage > 90) return 'error';
    if (percentage > 75) return 'warning';
    return 'success';
  };

  // Filter and sort projects - now done client-side after API data is loaded
  useEffect(() => {
    if (!projects.length) {
      setFilteredProjects([]);
      return;
    }

    let filtered = projects.filter(project => {
      const matchesStatus = statusFilter === 'all' || project.status === statusFilter;
      const matchesClient = clientFilter === 'all' || project.client === clientFilter;
      const matchesSearch = project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           project.client.toLowerCase().includes(searchTerm.toLowerCase());
      
      return matchesStatus && matchesClient && matchesSearch;
    });

    // Sort projects
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'budget':
          return b.budget - a.budget;
        case 'progress':
          return b.progress - a.progress;
        case 'endDate':
          return a.endDate.getTime() - b.endDate.getTime();
        default:
          return 0;
      }
    });

    setFilteredProjects(filtered);
  }, [projects, statusFilter, clientFilter, searchTerm, sortBy]);

  const uniqueClients = Array.from(new Set(projects.map(p => p.client)));

  // Show loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Show error state
  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Assignment color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Total Projects</Typography>
              </Box>
              <Typography variant="h4" color="primary">
                {projectSummary.totalProjects}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {projectSummary.activeProjects} active, {projectSummary.completedProjects} completed
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AttachMoney color="success" sx={{ mr: 1 }} />
                <Typography variant="h6">Budget Overview</Typography>
              </Box>
              <Typography variant="h4" color="success.main">
                {formatCurrency(projectSummary.totalBudget)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatCurrency(projectSummary.totalSpent)} spent ({projectSummary.budgetUtilization.toFixed(1)}%)
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Schedule color="info" sx={{ mr: 1 }} />
                <Typography variant="h6">Average Progress</Typography>
              </Box>
              <Typography variant="h4" color="info.main">
                {projectSummary.averageProgress.toFixed(1)}%
              </Typography>
              <LinearProgress
                variant="determinate"
                value={projectSummary.averageProgress}
                sx={{ mt: 1 }}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CheckCircle color="success" sx={{ mr: 1 }} />
                <Typography variant="h6">On-Time Delivery</Typography>
              </Box>
              <Typography variant="h4" color="success.main">
                {projectSummary.onTimeDelivery}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Based on historical data
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters and Search */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Project Filters
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              label="Search Projects"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="planning">Planning</MenuItem>
                <MenuItem value="on-hold">On Hold</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Client</InputLabel>
              <Select
                value={clientFilter}
                label="Client"
                onChange={(e) => setClientFilter(e.target.value)}
              >
                <MenuItem value="all">All Clients</MenuItem>
                {uniqueClients.map(client => (
                  <MenuItem key={client} value={client}>{client}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Sort By</InputLabel>
              <Select
                value={sortBy}
                label="Sort By"
                onChange={(e) => setSortBy(e.target.value)}
              >
                <MenuItem value="name">Name</MenuItem>
                <MenuItem value="budget">Budget</MenuItem>
                <MenuItem value="progress">Progress</MenuItem>
                <MenuItem value="endDate">End Date</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<GetApp />}
              size="small"
            >
              Export
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Projects Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Project</TableCell>
              <TableCell>Client</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Progress</TableCell>
              <TableCell>Budget</TableCell>
              <TableCell>Team</TableCell>
              <TableCell>End Date</TableCell>
              <TableCell>Health</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredProjects.map((project) => (
              <TableRow key={project.id} hover>
                <TableCell>
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {project.name}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                      <Chip
                        size="small"
                        label={project.priority}
                        color={getPriorityColor(project.priority) as any}
                        variant="outlined"
                      />
                      <Chip
                        size="small"
                        label={project.category}
                        variant="outlined"
                      />
                    </Box>
                  </Box>
                </TableCell>
                
                <TableCell>
                  <Typography variant="body2">
                    {project.client}
                  </Typography>
                </TableCell>
                
                <TableCell>
                  <Chip
                    label={project.status}
                    color={getStatusColor(project.status) as any}
                    size="small"
                  />
                </TableCell>
                
                <TableCell>
                  <Box sx={{ minWidth: 100 }}>
                    <Typography variant="body2" gutterBottom>
                      {project.progress}%
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={project.progress}
                      color={project.progress >= 75 ? 'success' : project.progress >= 50 ? 'info' : 'warning'}
                    />
                  </Box>
                </TableCell>
                
                <TableCell>
                  <Box>
                    <Typography variant="body2" fontWeight="bold">
                      {formatCurrency(project.budget)}
                    </Typography>
                    <Typography
                      variant="caption"
                      color={getBudgetStatus(project.budget, project.spent)}
                    >
                      {formatCurrency(project.spent)} spent
                    </Typography>
                  </Box>
                </TableCell>
                
                <TableCell>
                  <AvatarGroup max={3} sx={{ justifyContent: 'flex-start' }}>
                    {project.teamMembers.map((member, index) => (
                      <Avatar
                        key={index}
                        sx={{ width: 24, height: 24, fontSize: '0.75rem' }}
                      >
                        {member.charAt(0).toUpperCase()}
                      </Avatar>
                    ))}
                  </AvatarGroup>
                </TableCell>
                
                <TableCell>
                  <Typography variant="body2">
                    {formatDate(project.endDate)}
                  </Typography>
                </TableCell>
                
                <TableCell>
                  <Chip
                    label={project.health}
                    color={getHealthColor(project.health) as any}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                
                <TableCell>
                  <Tooltip title="View Details">
                    <IconButton size="small">
                      <Visibility />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {filteredProjects.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="text.secondary">
            No projects found matching your criteria
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ProjectReports;